package com.essntl.features.proposal.ui.selectservice.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.unit.dp
import com.essntl.core.utils.viewmodel.State
import com.essntl.features.proposal.domain.model.ProposalServiceModel
import com.essntl.features.proposal.ui.selectservice.SelectServiceNavigation
import com.essntl.features.service.domain.model.service.ServiceModel
import com.essntl.features.service.ui.components.ServiceHeaderSection
import com.essntl.features.service.ui.components.richtext.ServiceRichTextEditor
import com.essntl.features.supplier.domain.model.SupplierModel
import com.essntl.uikit.components.button.secondary.EsMediumSecondaryButton
import com.essntl.uikit.components.button.secondary.EsSmallSecondaryButton
import com.essntl.uikit.components.checkbox.EsPrimaryCheckBox
import com.essntl.uikit.components.dropdown.EsDropdownMenuItem
import com.essntl.uikit.components.dropdown.EsSearchableDropDown
import com.essntl.uikit.components.expandable.ExpandableTabs
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import com.mohamedrejeb.richeditor.model.rememberRichTextState

@Composable
fun ProposalServiceInfos(
    service: ServiceModel,
    proposalService: ProposalServiceModel,
    getAllSupplier: State<List<SupplierModel>>,
    isLoading: Boolean,
    isEnabled: Boolean,
    showArrow: Boolean = false,
    onArrowClick: () -> Unit = {},
    onProposalServiceChanged: (proposalService: ProposalServiceModel) -> Unit,
    onNavigation: (SelectServiceNavigation) -> Unit,
) {
    Column(
        modifier = Modifier
            .padding(top = 1.5f.spacing)
            .fillMaxSize()
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .background(MaterialTheme.esColorScheme.bgSecondary),
    ) {

        ServiceHeaderSection(
            title = service.title,
            headline = service.headline,
            referenceCode = service.referenceCode,
            showArrow = showArrow,
            onArrowClick = onArrowClick,
            modifier = Modifier
                .padding(top = 1.5.spacing)
                .padding(horizontal = 2.5.spacing),
        )

        Column(
            verticalArrangement = Arrangement.spacedBy(2.5f.spacing),
            modifier = Modifier
                .padding(1.5f.spacing)
                .verticalScroll(rememberScrollState()),
        ) {
            EsInput(
                value = service.internalNotes ?: "No notes here",
                onValueChange = {},
                placeholder = "No notes here",
                singleLine = false,
                label = "Internal notes",
                enabled = false,
            )

            EsInput(
                value = proposalService.additionalInfo ?: "",
                onValueChange = {
                    onProposalServiceChanged(
                        proposalService.copy(
                            additionalInfo = it,
                        ),
                    )
                },
                placeholder = "Enter additional info",
                singleLine = false,
                label = "Additional info",
                enabled = !isLoading && isEnabled,
            )

            Row(
                horizontalArrangement = Arrangement.spacedBy(2.spacing),
            ) {
                Row(
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    EsPrimaryCheckBox(
                        checked = proposalService.commissionable == true,
                        enabled = isEnabled,
                        onCheckedChange = {
                            onProposalServiceChanged(
                                proposalService.copy(
                                    commissionable = it,
                                ),
                            )
                        },
                        modifier = Modifier
                            .focusProperties { canFocus = false }
                            .align(Alignment.CenterVertically),
                    )

                    Spacer(modifier = Modifier.width(1.spacing))

                    Text(
                        text = "Commissionable",
                        style = MaterialTheme.esTypography.p1Regular16,
                        color = MaterialTheme.esColorScheme.textPrimary,
                    )
                }

                Row(
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    EsPrimaryCheckBox(
                        checked = proposalService.bookedByTa ?: false,
                        enabled = isEnabled,
                        onCheckedChange = {
                            onProposalServiceChanged(
                                proposalService.copy(
                                    bookedByTa = it,
                                ),
                            )
                        },
                        modifier = Modifier
                            .focusProperties { canFocus = false }
                            .align(Alignment.CenterVertically),
                    )

                    Spacer(modifier = Modifier.width(1.spacing))

                    Text(
                        text = "Booked by Travel Agent",
                        style = MaterialTheme.esTypography.p1Regular16,
                        color = MaterialTheme.esColorScheme.textPrimary,
                    )
                }
            }

            EsSearchableDropDown(
                itemList = getAllSupplier.data.orEmpty(),
                isLoading = getAllSupplier.isLoading,
                itemName = proposalService.supplier?.prettyName ?: "",
                findItem = { supplier, searchText ->
                    supplier.prettyName.contains(searchText, ignoreCase = true)
                },
                emptyListText = "No suppliers found!",
                placeholder = "Select Supplier",
                enabled = isEnabled,
                modifier = Modifier.width(400.dp),
                content = { focusManager, isDropDownExpanded, filteredSupplierList ->
                    filteredSupplierList.forEach { supplier ->
                        key(supplier.id) {
                            Box(
                                modifier =
                                    Modifier
                                        .height(40.dp),
                            ) {
                                EsDropdownMenuItem(
                                    text = supplier.prettyName,
                                    onClick = {
                                        onProposalServiceChanged(
                                            proposalService.copy(
                                                supplier = supplier,
                                            ),
                                        )
                                        isDropDownExpanded.value = false
                                        focusManager.clearFocus()
                                    },
                                    modifier = Modifier.fillMaxHeight(),
                                )
                            }
                        }
                    }
                },
            )
            Spacer(modifier = Modifier.height(2.spacing))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(1.dp),
                ) {
                    Text(
                        text = "Editing this description will affect only this proposal.",
                        color = MaterialTheme.esColorScheme.primary,
                        style = MaterialTheme.esTypography.p1Regular16.copy(fontStyle = FontStyle.Italic),
                        modifier = Modifier
                            .weight(1f)
                            .wrapContentWidth(Alignment.Start),
                    )

                    EsMediumSecondaryButton(
                        text = "Edit main service instead",
                        enabled = isEnabled,
                        onClick = {
                            onNavigation(
                                SelectServiceNavigation.EditService(
                                    service = service,
                                    enableEdit = true,
                                ),
                            )
                        },
                        modifier = Modifier.wrapContentWidth(),
                    )
                }

                val richTextState = rememberRichTextState()
                val richTextStateServiceDescription = rememberRichTextState()

                LaunchedEffect(proposalService.id) {
                    // saves the main service description in a rich text state to use when user clicks "reset description" button
                    richTextStateServiceDescription.setHtml(service.description ?: "")

                    if (proposalService.editedDescription.isNullOrEmpty()) {
                        richTextState.clear()
                    } else {
                        proposalService.editedDescription?.let {
                            richTextState.setHtml(it)
                        }
                    }
                }

                LaunchedEffect(richTextState.annotatedString) {
                    if (richTextState.annotatedString.isNotEmpty()) {
                        onProposalServiceChanged(
                            proposalService.copy(
                                editedDescription = richTextState.toHtml(),
                            ),
                        )
                    }
                }
                ServiceRichTextEditor(
                    richTextState = richTextState,
                    enabled = isEnabled,
                    modifier = Modifier.fillMaxWidth(),
                    onRewriteWithAI = {},
                )

                EsSmallSecondaryButton(
                    text = "Reset to main description",
                    enabled =
                        richTextState.annotatedString.text !=
                            richTextStateServiceDescription.annotatedString.text && isEnabled,
                    onClick = {
                        richTextState.setHtml(service.description ?: "")
                        richTextStateServiceDescription.setHtml(service.description ?: "")
                    },
                )
        }

//        val tabList = remember { listOf("Details") }

//        ExpandableTabs(
//            tabList = tabList,
//            id = proposalService.id,
//            expandable = false,
//            modifier = Modifier.fillMaxWidth(),
//        ) { index ->
//
//        }
    }
}